import React, { useState } from "react";
import axios from "axios";
import BusyButton from "../components/basic/busy-button";
import Checkbox, { CheckboxIcon } from "../components/basic/checkbox";
import { apiHttpUrl } from "../helpers/api-base";
import type { AccountRow } from "../params/types";
import { getAuthToken, withAuthToken } from "../helpers/auth";

type Group = "avatar" | "photos" | "videos" | "reels";

interface MigrationFile {
  name: string;
  kind: "image" | "video";
  size: number;
}

interface MigrationResult {
  ok: boolean;
  error?: string;
  warning?: string;
  username: string;
  groups: Record<Group, MigrationFile[]>;
}

const LIMIT_OPTIONS = [10, 30, 50, 100, 200, 500, 750, 1000];

const USERNAME_RE = /^[A-Za-z0-9._]{1,30}$/;

const GROUP_DEFS: { id: Group; label: string }[] = [
  { id: "avatar", label: "Avatar" },
  { id: "photos", label: "Photos" },
  { id: "videos", label: "Videos" },
  { id: "reels", label: "Reels" },
];

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fileUrl(username: string, group: Group, name: string): string {
  return withAuthToken(
    apiHttpUrl(
      `/api/migration/file?username=${encodeURIComponent(username)}` +
        `&group=${group}&name=${encodeURIComponent(name)}`,
    ),
  );
}

/** Small cached preview (resized image / first video frame). */
function thumbUrl(username: string, group: Group, name: string): string {
  return withAuthToken(
    apiHttpUrl(
      `/api/migration/thumb?username=${encodeURIComponent(username)}` +
        `&group=${group}&name=${encodeURIComponent(name)}`,
    ),
  );
}

interface MigrationDownloaderProps {
  accounts?: AccountRow[];
  setError?: (message: string) => void;
}

export default function MigrationDownloader({ accounts = [], setError }: MigrationDownloaderProps) {
  const [username, setUsername] = useState("");
  const [account, setAccount] = useState("");
  const [maxPosts, setMaxPosts] = useState(10);
  const [enabled, setEnabled] = useState<Record<Group, boolean>>({
    avatar: true,
    photos: true,
    videos: true,
    reels: true,
  });
  const [skipExisting, setSkipExisting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);

  const cleanUsername = username.trim().replace(/^@/, "");
  const formValid =
    USERNAME_RE.test(cleanUsername) &&
    Boolean(account) &&
    GROUP_DEFS.some((g) => enabled[g.id]);

  const onFetch = async () => {
    if (!formValid || busy) return;
    setBusy(true);
    try {
      const res = await axios.post<MigrationResult>(
        apiHttpUrl("/api/migration/fetch"),
        {
          username: cleanUsername,
          account,
          max_posts: maxPosts,
          skip_existing: skipExisting,
          groups: GROUP_DEFS.filter((g) => enabled[g.id]).map((g) => g.id),
        },
        { headers: authHeaders(), validateStatus: () => true, timeout: 0 },
      );
      if (res.data?.error) setError?.(res.data.error);
      setResult(res.data?.groups ? res.data : null);
    } catch {
      setError?.("Unable to reach the server.");
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") void onFetch();
  };

  return (
    <div className="ib-migration-view">
      <div className="ib-settings-section ib-migration-card">
        <div className="ib-migration-bar">
          <input
            className="ib-migration-input"
            type="text"
            placeholder="Instagram username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={busy}
            spellCheck={false}
            autoCapitalize="off"
          />
          <label className="ib-migration-limit">
            <span>Account</span>
            <select
              className="ib-migration-select"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              disabled={busy}
            >
              <option value="">Select account…</option>
              {accounts
                .filter((a) => a.session_status !== "banned")
                .map((a) => (
                  <option key={a.username} value={a.username}>
                    {a.username}
                    {a.ig_username ? ` (@${a.ig_username})` : ""}
                  </option>
                ))}
            </select>
          </label>
          <label className="ib-migration-limit">
            <span>Latest posts</span>
            <select
              className="ib-migration-select"
              value={maxPosts}
              onChange={(e) => setMaxPosts(Number(e.target.value))}
              disabled={busy}
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <Checkbox
            checked={skipExisting}
            disabled={busy}
            label="Skip already downloaded"
            onChange={setSkipExisting}
          />
          <BusyButton
            variant="jf-btn jf-btn-primary ib-migration-btn"
            label="Fetch"
            busy={busy}
            busyLabel="Downloading…"
            disabled={!formValid}
            onClick={onFetch}
          />
        </div>
        <div className="ib-migration-types">
          {GROUP_DEFS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              disabled={busy}
              className={`ib-tab-btn ib-migration-chip${
                enabled[id] ? " ib-tab-btn--active" : ""
              }`}
              onClick={() => setEnabled((p) => ({ ...p, [id]: !p[id] }))}
            >
              <CheckboxIcon checked={enabled[id]} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {result && (
        <>
          <p className="ib-migration-meta">
            Saved to <code>downloads/{result.username}</code>
            {result.warning ? ` — ${result.warning}` : ""}
          </p>
          {GROUP_DEFS.map(({ id, label }) => {
            const files = result.groups[id] || [];
            return (
              <section key={id} className="ib-migration-group">
                <h3 className="ib-migration-group__title">
                  {label} <span>({files.length})</span>
                </h3>
                {files.length === 0 ? (
                  <p className="ib-migration-empty">Nothing found.</p>
                ) : (
                  <div className="ib-migration-grid">
                    {files.map((f) => {
                      const src = fileUrl(result.username, id, f.name);
                      return (
                        <a
                          key={f.name}
                          className="ib-migration-item"
                          href={src}
                          target="_blank"
                          rel="noreferrer"
                          title={f.name}
                        >
                          <img
                            src={thumbUrl(result.username, id, f.name)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                          />
                          {f.kind === "video" && (
                            <span className="ib-play" aria-hidden="true" />
                          )}
                        </a>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
